/**
 * 中継ストリーム → 正規化データモデルへの変換。
 *
 * ここが「既存プロトコル」と「次世代のデータモデル」の境界になる。
 *
 * 設計上の要点
 *  1. **未知のセンテンスは捨てず `passthrough` として返す**（docs/03 §5）。
 *     中継ストリームは TV など他クライアントと共有された資源である。
 *  2. **RMC だけに依存しない**。実ログでは RMC がほとんど出力されておらず、
 *     位置は $GNS / $GGA、速度・方位は $VTG が主供給源だった（docs/01 §11）。
 *  3. 同じ UTC 秒に属するセンテンス群を1つの測位にまとめる。
 */

import { isPlausibleAbsoluteMs, TerminalClock } from './clock.ts';
import { parseLines } from './line.ts';
import { parseGga, parseGns, parseGst, parseRmc, parseVtg, parseZda } from './nmea.ts';
import { parseMark, parseTvCircle, parseTvMark } from './sentences.ts';
import type { CircleArea, FixSample, GvLine, MarkPoint, SourceKind } from './types.ts';

export interface DecodeOptions {
  /** 受信時刻（UTC epoch ms）。時刻の手掛かりが無い行の代用に使う */
  receivedAtMs?: number;
  /** UTM ゾーン。$_TVMARK / $_TVCIRCLE の復元に必要（センテンスに含まれていない） */
  zone?: number;
  /** 既定の出所 */
  source?: SourceKind;
}

export interface DecodeResult {
  fixes: FixSample[];
  marks: Map<string, MarkPoint[]>;
  markNames: Map<string, string[]>;
  circles: Map<string, CircleArea[]>;
  /** 解釈しなかった行。**捨ててはならない**ので呼び出し側へ返す */
  passthrough: GvLine[];
  /** 種別ごとの行数（デバッグ・監視用） */
  counts: Map<string, number>;
}

/** 組み立て途中の測位。同じ UTC 秒のセンテンスをここへ集約する */
interface Epoch {
  utcSeconds: number;
  elapsedMs: number;
  lat: number | null;
  lon: number | null;
  altMsl: number | null;
  speed: number | null;
  heading: number | null;
  /** RMC / ZDA から日付が確定したときの絶対時刻 */
  absoluteMs: number | null;
}

/** 端末ごとに持ち越す状態 */
interface TerminalState {
  epoch: Epoch | null;
  /** 直近の高度。同じ秒に高度センテンスが無い場合に補う */
  lastAltMsl: number | null;
  /** 直近の精度（$GST 由来） */
  accuracy: number | null;
  altAccuracy: number | null;
  /** 直近に判明した日付（UTC 0時のミリ秒） */
  dayStartMs: number | null;
}

export class StreamDecoder {
  readonly clock: TerminalClock;
  #states = new Map<string, TerminalState>();

  constructor(clock: TerminalClock = new TerminalClock()) {
    this.clock = clock;
  }

  /**
   * 保持している状態を空にする。
   *
   * ★ これが無いと、`dayStartMs`（RMC/ZDAから学習した日付）と
   *   `clock`（経過ms→UTCのオフセット）が呼び出し側の「リセット」を
   *   すり抜けて残ってしまう。壊れた日付を一度学習してしまった端末は、
   *   運用者が手動リセット（`POST /api/reset`）しても直らず、
   *   サーバー再起動が必須になっていた（実際に確認した不具合）。
   */
  reset(terminalId?: string): void {
    if (terminalId) this.#states.delete(terminalId);
    else this.#states.clear();
    this.clock.reset(terminalId);
  }

  #stateOf(tid: string): TerminalState {
    let s = this.#states.get(tid);
    if (!s) {
      s = { epoch: null, lastAltMsl: null, accuracy: null, altAccuracy: null, dayStartMs: null };
      this.#states.set(tid, s);
    }
    return s;
  }

  decode(text: string, options: DecodeOptions = {}): DecodeResult {
    const receivedAtMs = options.receivedAtMs ?? Date.now();
    const source = options.source ?? 'balloon';
    const result: DecodeResult = {
      fixes: [],
      marks: new Map(),
      markNames: new Map(),
      circles: new Map(),
      passthrough: [],
      counts: new Map(),
    };

    const lines = parseLines(text);

    // 1巡目: 絶対時刻を持つセンテンスで先に時計を合わせる。
    // こうしないと同じ受信バッチ内の先行行に時刻を与えられない。
    // ★ observeAbsolute() 側で明らかにおかしい日付（RTC未同期の端末などが
    //   まれに送ってくる）は自動的に無視される（clock.ts の isPlausibleAbsoluteMs 参照）
    for (const line of lines) {
      const rmc = parseRmc(line.sentence);
      if (rmc?.utcDate && rmc.utcSeconds !== null) {
        const { year, month, day } = rmc.utcDate;
        this.clock.observeAbsolute(
          line.terminalId,
          line.elapsedMs,
          Date.UTC(year, month - 1, day) + rmc.utcSeconds * 1000,
          receivedAtMs,
        );
        continue;
      }
      const zda = parseZda(line.sentence);
      if (zda?.t != null) {
        this.clock.observeAbsolute(line.terminalId, line.elapsedMs, zda.t, receivedAtMs);
      }
    }

    // 2巡目: 内容を取り出す
    for (const line of lines) {
      result.counts.set(line.type, (result.counts.get(line.type) ?? 0) + 1);
      this.#consume(line, result, receivedAtMs, source, options);
    }

    // 積み残しの測位を吐き出す
    for (const [tid, s] of this.#states) {
      if (s.epoch) {
        this.#flush(tid, s, result, receivedAtMs, source);
      }
    }

    result.fixes.sort((a, b) => a.t - b.t);
    return result;
  }

  #consume(
    line: GvLine,
    result: DecodeResult,
    receivedAtMs: number,
    source: SourceKind,
    options: DecodeOptions,
  ): void {
    const tid = line.terminalId;
    const s = this.#stateOf(tid);
    const kind = line.type.slice(3); // 'GGA' | 'RMC' | ... （トーカーIDを除く）

    if (line.type.startsWith('$G')) {
      switch (kind) {
        case 'ZDA': {
          const zda = parseZda(line.sentence);
          // ★ 明らかにおかしい日付（RTC未同期の端末など）は採用しない。
          //   採用してしまうと、以後その端末の全測位がその日付に固定される
          //   （clock.ts の isPlausibleAbsoluteMs 参照）
          if (zda?.t != null && isPlausibleAbsoluteMs(zda.t, receivedAtMs)) {
            s.dayStartMs = Math.floor(zda.t / 86400000) * 86400000;
          }
          return;
        }
        case 'GST': {
          const gst = parseGst(line.sentence);
          if (gst) {
            s.accuracy =
              gst.latSigma !== null && gst.lonSigma !== null
                ? Math.hypot(gst.latSigma, gst.lonSigma)
                : null;
            s.altAccuracy = gst.altSigma;
          }
          return;
        }
        case 'GGA': {
          const gga = parseGga(line.sentence);
          if (!gga) return;
          if (gga.altMsl !== null) s.lastAltMsl = gga.altMsl;
          // 測位できていない行（fixQuality=0）は位置として採用しない
          const usable = gga.fixQuality !== null && gga.fixQuality > 0;
          const e = this.#epoch(tid, s, gga.utcSeconds, line.elapsedMs, result, receivedAtMs, source);
          if (e && usable) {
            if (gga.lat !== null) e.lat = gga.lat;
            if (gga.lon !== null) e.lon = gga.lon;
            if (gga.altMsl !== null) e.altMsl = gga.altMsl;
          }
          return;
        }
        case 'GNS': {
          const gns = parseGns(line.sentence);
          if (!gns) return;
          if (gns.altMsl !== null) s.lastAltMsl = gns.altMsl;
          // モードが全て 'N'（無測位）の行は採用しない
          const usable = gns.mode !== '' && /[^N]/.test(gns.mode);
          const e = this.#epoch(tid, s, gns.utcSeconds, line.elapsedMs, result, receivedAtMs, source);
          if (e && usable) {
            if (gns.lat !== null) e.lat = gns.lat;
            if (gns.lon !== null) e.lon = gns.lon;
            if (gns.altMsl !== null) e.altMsl = gns.altMsl;
          }
          return;
        }
        case 'RMC': {
          const rmc = parseRmc(line.sentence);
          if (!rmc) return;
          if (rmc.utcDate) {
            const candidate = Date.UTC(rmc.utcDate.year, rmc.utcDate.month - 1, rmc.utcDate.day);
            // ★ 明らかにおかしい日付は採用しない（ZDAと同じ理由。clock.ts参照）。
            //   これまでに正しい日付を学習済みなら、それを上書きしない
            if (isPlausibleAbsoluteMs(candidate, receivedAtMs)) s.dayStartMs = candidate;
          }
          const e = this.#epoch(tid, s, rmc.utcSeconds, line.elapsedMs, result, receivedAtMs, source);
          if (e && rmc.valid) {
            if (rmc.lat !== null) e.lat = rmc.lat;
            if (rmc.lon !== null) e.lon = rmc.lon;
            if (rmc.speed !== null) e.speed = rmc.speed;
            if (rmc.heading !== null) e.heading = rmc.heading;
            if (s.dayStartMs !== null && rmc.utcSeconds !== null) {
              e.absoluteMs = s.dayStartMs + rmc.utcSeconds * 1000;
            }
          }
          return;
        }
        case 'VTG': {
          // VTG は時刻を持たないので、進行中の測位へ足す
          const vtg = parseVtg(line.sentence);
          if (vtg && s.epoch) {
            if (vtg.speed !== null) s.epoch.speed = vtg.speed;
            if (vtg.trackTrue !== null) s.epoch.heading = vtg.trackTrue;
          }
          return;
        }
        case 'GSV':
        case 'GSA':
        case 'GLL':
        case 'GRS':
        case 'GBS':
        case 'TXT':
          // 測位品質の詳細。現時点では使わないが、素通しにも回さない（量が多いため）
          return;
      }
    }

    switch (line.type) {
      case '$_MARK': {
        const names = parseMark(line.sentence);
        if (names) result.markNames.set(tid, names);
        return;
      }
      case '$_TVMARK': {
        if (options.zone === undefined) {
          // ゾーンが分からないと座標に直せない。捨てずに素通しへ回す
          result.passthrough.push(line);
          return;
        }
        const marks = parseTvMark(line.sentence, options.zone);
        if (marks) result.marks.set(tid, marks.map((m) => ({ ...m, terminalId: tid })));
        return;
      }
      case '$_TVCIRCLE': {
        if (options.zone === undefined) {
          result.passthrough.push(line);
          return;
        }
        const circles = parseTvCircle(line.sentence, options.zone);
        if (circles) result.circles.set(tid, circles.map((c) => ({ ...c, terminalId: tid })));
        return;
      }
      default:
        // ★ 知らないセンテンスは捨てない
        result.passthrough.push(line);
    }
  }

  /**
   * 指定の UTC 秒に対応する測位を返す。
   * 秒が変わっていたら、それまでの測位を確定して吐き出す。
   */
  #epoch(
    tid: string,
    s: TerminalState,
    utcSeconds: number | null,
    elapsedMs: number,
    result: DecodeResult,
    receivedAtMs: number,
    source: SourceKind,
  ): Epoch | null {
    if (utcSeconds === null) return s.epoch;
    if (s.epoch && s.epoch.utcSeconds === utcSeconds) return s.epoch;
    if (s.epoch) this.#flush(tid, s, result, receivedAtMs, source);
    s.epoch = {
      utcSeconds,
      elapsedMs,
      lat: null,
      lon: null,
      altMsl: null,
      speed: null,
      heading: null,
      absoluteMs: null,
    };
    return s.epoch;
  }

  /** 組み立て中の測位を確定させる。位置が無いものは捨てる */
  #flush(
    tid: string,
    s: TerminalState,
    result: DecodeResult,
    receivedAtMs: number,
    source: SourceKind,
  ): void {
    const e = s.epoch;
    s.epoch = null;
    if (!e || e.lat === null || e.lon === null) return;

    let t: number;
    let timeQuality: FixSample['timeQuality'];
    if (e.absoluteMs !== null) {
      t = e.absoluteMs;
      timeQuality = 'measured';
    } else if (s.dayStartMs !== null) {
      t = s.dayStartMs + e.utcSeconds * 1000;
      timeQuality = 'measured';
    } else {
      const resolved = this.clock.resolve(tid, e.elapsedMs, receivedAtMs);
      t = resolved?.t ?? receivedAtMs;
      timeQuality = 'derived';
    }

    result.fixes.push({
      terminalId: tid,
      t,
      timeQuality,
      lat: e.lat,
      lon: e.lon,
      altMsl: e.altMsl ?? s.lastAltMsl,
      speed: e.speed,
      heading: e.heading,
      accuracy: s.accuracy,
      altAccuracy: s.altAccuracy,
      source,
    });
  }
}
